import { useTranslation } from "react-i18next";
import { Chip, StatusGlyph } from "@outpave/ds";
import { InvoiceStatus } from "../../enums/InvoiceStatus";
import DisputeStatus from "../../enums/DisputeStatus";
import {
  invoiceDisplayStatus,
  invoiceStatusLabelKey,
  invoiceStatusTone,
} from "utils/invoices/invoiceStatus";

interface RenderInvoiceStatusProps {
  invoiceStatus: InvoiceStatus | string;
  disputeStatus?: DisputeStatus | string | null;
  dueDate?: Date | string | null;
  microdepositPending?: boolean;
}

/**
 * One invoice-status pill for every surface that draws one.
 *
 * PAID and COMPLETE used to render as a hand-styled line of grey text instead
 * of a chip, and that branch tested the raw status below the dispute
 * resolution, so a disputed paid invoice read "Disputed" in the drawer and
 * "Paid" in the table.
 */
const RenderInvoiceStatus: React.FC<RenderInvoiceStatusProps> = ({
  invoiceStatus,
  disputeStatus,
  dueDate,
  microdepositPending = false,
}) => {
  const { t } = useTranslation();

  const displayStatus = invoiceDisplayStatus({
    status: invoiceStatus,
    disputeStatus,
    dueDate,
    microdepositPending,
  });
  const labelKey = invoiceStatusLabelKey(displayStatus);
  const tone = invoiceStatusTone(displayStatus);

  // StatusGlyph is looked up by the untranslated label, so the glyph is right
  // in every locale while the label renders translated.
  return (
    <Chip
      tone={tone}
      size="sm"
      leading={<StatusGlyph status={labelKey} tone={tone} />}
    >
      {t(labelKey)}
    </Chip>
  );
};

export default RenderInvoiceStatus;
